import { z } from 'zod'

/**
 * Chat thread realtime contract — the shape of what the agent transmits into
 * a chat thread and what the SPA renders. Shared by the Worker (chat bridge →
 * ChatStore DO) and the web client (use-chat-collab).
 *
 * Transport: every chat message is a row in the ChatStore. Scalar fields are
 * stored as-is; the structured fields listed in CHAT_MESSAGE_JSON_FIELDS are
 * stored as JSON strings (the agent overwrites them wholesale on every
 * flush). The store's meta carries the live AgentStatus.
 *
 * Legacy fields (`toolCalls`, `taskActions`, `documentActions`) written by
 * the pre-elements bridge are still read by the client and mapped onto
 * steps/artifacts, so migrated threads keep rendering.
 */

// ── Agent activity steps (ordered timeline inside one assistant message) ────

export const agentStepStatusSchema = z.enum(['running', 'completed', 'error'])
export type AgentStepStatus = z.infer<typeof agentStepStatusSchema>

/** Model reasoning ("thinking") block streamed before/between tool calls. */
export const reasoningStepSchema = z.object({
  kind: z.literal('reasoning'),
  id: z.string(),
  text: z.string(),
  status: agentStepStatusSchema,
  startedAt: z.string(),
  completedAt: z.string().optional(),
})
export type ReasoningStep = z.infer<typeof reasoningStepSchema>

export const toolStepErrorSchema = z.object({
  message: z.string(),
  /** Whether re-running the turn is likely to succeed. */
  retryable: z.boolean().optional(),
})

/** Render-ready digest of a tool result (bridge-computed, tool-specific). */
export const toolResultSummarySchema = z.object({
  /** A tool-reported failure ({ error } output) — the call itself succeeded. */
  error: z.string().optional(),
  /** Document touched/read (readDocument, createDocument, updateDocument). */
  document: z
    .object({
      id: z.string(),
      title: z.string(),
      slug: z.string().optional(),
      createdAt: z.string().optional(),
      updatedAt: z.string().optional(),
      chars: z.number().optional(),
      words: z.number().optional(),
      excerpt: z.string().optional(),
    })
    .optional(),
  /** Task created/updated. */
  task: z
    .object({
      id: z.string(),
      title: z.string(),
      columnName: z.string().optional(),
      priority: z.number().optional(),
    })
    .optional(),
  /** List-style results: how many, and a few labels (titles, names, files…). */
  count: z.number().optional(),
  items: z.array(z.string()).optional(),
  /** Spreadsheet header line (readSpreadsheetCsv). */
  header: z.string().optional(),
  /** Short trailing note appended to the summary (currently unused). */
  note: z.string().optional(),
})
export type ToolResultSummary = z.infer<typeof toolResultSummarySchema>

/** One tool invocation: arguments while streaming, result/error when settled. */
export const toolStepSchema = z.object({
  kind: z.literal('tool'),
  id: z.string(),
  toolName: z.string(),
  /** Parsed arguments (may be partial while `argsText` is still streaming). */
  args: z.unknown().optional(),
  /** Raw argument JSON as streamed by the model. */
  argsText: z.string().optional(),
  status: agentStepStatusSchema,
  /** JSON preview of the tool's own return value (≤ 1500 chars; may be truncated). */
  resultPreview: z.string().optional(),
  /**
   * Compact, render-ready digest of the result computed by the bridge from
   * the full output (the preview above may be cut mid-JSON for long
   * results). Shape per tool — see summarizeToolOutput in chat-bridge.
   */
  resultSummary: toolResultSummarySchema.optional(),
  error: toolStepErrorSchema.optional(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
})
export type ToolStep = z.infer<typeof toolStepSchema>

export const agentStepSchema = z.discriminatedUnion('kind', [reasoningStepSchema, toolStepSchema])
export type AgentStep = z.infer<typeof agentStepSchema>

// ── Artifacts (things the agent created or changed in the project) ──────────

export const agentArtifactStatusSchema = z.enum(['writing', 'done', 'error'])

export const agentArtifactSchema = z.object({
  kind: z.enum(['task', 'document']),
  action: z.enum(['created', 'updated']),
  /** Project entity id once known (documents/tasks); the tool step id before that. */
  id: z.string(),
  title: z.string(),
  status: agentArtifactStatusSchema,
  /** Tool step that produced it — links the card to the timeline entry. */
  stepId: z.string().optional(),
  slug: z.string().optional(),
  columnName: z.string().optional(),
  priority: z.number().optional(),
  /** Rough size hint while writing (characters of the draft so far). */
  chars: z.number().optional(),
  /** Blocks touched in a block document (PLAN-079) — deep-link + undo targets. */
  blockIds: z.array(z.string()).optional(),
  /** Agent turn id the document ops were logged under (undo per turn). */
  turnId: z.string().optional(),
})
export type AgentArtifact = z.infer<typeof agentArtifactSchema>

/**
 * One card per project entity (board 1s4a). A reply carries one artifact per
 * tool call — two `updateTask` calls on the same task are two artifacts — and
 * the stored list keeps that (the timeline and error marking key on
 * `stepId`), so every place that *displays* references folds it here:
 * first position wins, the latest call supplies title/column/size, "created"
 * beats "updated", a call still writing keeps the card writing, and the
 * touched blocks are unioned (the first one stays the deep-link target).
 */
export function uniqueArtifacts(artifacts: readonly AgentArtifact[]): AgentArtifact[] {
  const byKey = new Map<string, AgentArtifact>()
  for (const artifact of artifacts) {
    const key = `${artifact.kind}:${artifact.id}`
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, artifact)
      continue
    }
    const blockIds = [...new Set([...(prev.blockIds ?? []), ...(artifact.blockIds ?? [])])]
    byKey.set(key, {
      ...artifact,
      stepId: prev.stepId ?? artifact.stepId,
      action: prev.action === 'created' ? 'created' : artifact.action,
      status: prev.status === 'writing' ? 'writing' : artifact.status,
      title: artifact.title || prev.title,
      ...(blockIds.length ? { blockIds } : {}),
    })
  }
  return [...byKey.values()]
}

// ── Citations ([source: <documentId>:<fileName>] markers in assistant text) ─

export const messageCitationSchema = z.object({
  /** 1-based, in order of first appearance. */
  index: z.number().int().positive(),
  documentId: z.string(),
  title: z.string(),
})
export type MessageCitation = z.infer<typeof messageCitationSchema>

/** Markers the agent prompt asks for; parsed by the bridge and the client. */
export const CITATION_MARKER = /\[source:\s*([^:\]]+):([^\]]+)\]/g

export function extractCitations(text: string): MessageCitation[] {
  const seen = new Map<string, MessageCitation>()
  for (const match of text.matchAll(CITATION_MARKER)) {
    const documentId = match[1].trim()
    if (seen.has(documentId)) continue
    seen.set(documentId, { index: seen.size + 1, documentId, title: match[2].trim() })
  }
  return [...seen.values()]
}

// ── Usage (aggregated over the model calls of one assistant reply) ──────────

export const messageUsageSchema = z.object({
  model: z.string().optional(),
  provider: z.string().optional(),
  /** Number of model calls (tool round-trips + final answer). */
  modelCalls: z.number().int().nonnegative(),
  inputTokens: z.number().nonnegative(),
  outputTokens: z.number().nonnegative(),
  cacheReadTokens: z.number().nonnegative(),
  cacheWriteTokens: z.number().nonnegative(),
  reasoningTokens: z.number().nonnegative(),
  totalTokens: z.number().nonnegative(),
  /** Provider-reported cost in USD, when available. */
  costUsd: z.number().nonnegative().optional(),
  /**
   * What the reply cost in credits — the LLM tokens plus the flat charges of
   * the tool calls it made. Written by the chat bridge from the same numbers
   * it books into the ledger, so the "…" menu can never drift from the bar.
   */
  credits: z
    .object({
      total: z.number().nonnegative(),
      llm: z.number().nonnegative(),
      tools: z.number().nonnegative(),
      toolCalls: z.number().int().nonnegative(),
      /** Which price the LLM part came from — see contracts/llm-pricing.ts. */
      costSource: z.enum(['table', 'provider', 'fallback']),
    })
    .optional(),
})
export type MessageUsage = z.infer<typeof messageUsageSchema>

// ── Errors ──────────────────────────────────────────────────────────────────

export const chatMessageErrorKindSchema = z.enum([
  /** The model/provider rejected the request (context length, unsupported input…). */
  'provider_capability',
  /** Transient provider failure (rate limit, timeout, 5xx). */
  'provider_transient',
  /** The agent crashed or was aborted. */
  'hard_error',
  /** The turn finished without any text or tool activity. */
  'empty_reply',
  /**
   * The turn stopped reporting progress and never finished (agent instance
   * lost mid-turn) — later steps such as tool calls may not have run.
   * Derived client-side from a stale busy status, never written by the bridge.
   */
  'interrupted',
  /**
   * A user stopped the reply (`chat.stopAgent`). The partial text and steps
   * are kept; the store discards any later update the bridge sends for it.
   */
  'stopped',
  'unknown',
])
export type ChatMessageErrorKind = z.infer<typeof chatMessageErrorKindSchema>

export const assistantMessageErrorSchema = z.object({
  kind: chatMessageErrorKindSchema,
  message: z.string().optional(),
  retryable: z.boolean(),
})
export type AssistantMessageError = z.infer<typeof assistantMessageErrorSchema>

// ── Live agent status (room-level `agentStatus` Y.Map) ──────────────────────

export const agentStateSchema = z.enum([
  'idle',
  /** Dispatched; the agent instance has not started the turn yet. */
  'queued',
  'thinking',
  'reasoning',
  'tool-calling',
  'responding',
  'error',
])
export type AgentState = z.infer<typeof agentStateSchema>

export const agentStatusSchema = z.object({
  state: agentStateSchema,
  /** Tool currently running (state === 'tool-calling'). */
  toolName: z.string().optional(),
  /** Assistant message being produced. */
  messageId: z.string().optional(),
  updatedAt: z.string().optional(),
  error: z.string().optional(),
})
export type AgentStatus = z.infer<typeof agentStatusSchema>

// ── Attachments on user messages ────────────────────────────────────────────

export const chatAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
  size: z.number(),
  contentType: z.string(),
  documentId: z.string().optional(),
  agentFilename: z.string().optional(),
})
export type ChatAttachment = z.infer<typeof chatAttachmentSchema>

/**
 * Attachments as listed (message chips, the workspace panel): one per
 * project document or stored file, first occurrence kept (board 1s4a).
 */
export function uniqueAttachments(attachments: readonly ChatAttachment[]): ChatAttachment[] {
  const seen = new Set<string>()
  return attachments.filter((att) => {
    const key = att.documentId ? `document:${att.documentId}` : `url:${att.url}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// ── Inbox e-mail reference on user messages (PLAN-084, board cik7) ──────────

/**
 * The e-mail an inbox run handed to the agent. The stored user message keeps
 * only the prompt plus this envelope — the SPA renders it as a card that
 * opens the mail in the inbox — while the agent's own input still carries
 * the full `[inbox-email]` block (`renderInboxEmailBlock`).
 */
export const chatEmailRefSchema = z.object({
  emailId: z.string(),
  fromName: z.string().nullable(),
  fromAddress: z.string(),
  subject: z.string().nullable(),
  receivedAt: z.string(),
  attachmentCount: z.number().int().nonnegative(),
})
export type ChatEmailRef = z.infer<typeof chatEmailRefSchema>

// ── Message shape (client view of one Y.Map) ────────────────────────────────

export const chatRoomMessageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  threadId: z.string(),
  createdAt: z.string(),
  /** Set by the agent when the turn finished (success or error). */
  completedAt: z.string().optional(),
  userId: z.string().optional(),
  userName: z.string().optional(),
  userAvatarUrl: z.string().optional(),
  attachments: z.array(chatAttachmentSchema).optional(),
  /** Inbox e-mail this (run) message is about — rendered as an envelope card. */
  email: chatEmailRefSchema.optional(),
  steps: z.array(agentStepSchema).optional(),
  artifacts: z.array(agentArtifactSchema).optional(),
  citations: z.array(messageCitationSchema).optional(),
  usage: messageUsageSchema.optional(),
  error: assistantMessageErrorSchema.optional(),
  deletedAt: z.string().optional(),
  deletedBy: z.string().optional(),
})
export type ChatRoomMessage = z.infer<typeof chatRoomMessageSchema>

/** Structured Y.Map fields, stored JSON-encoded. */
export const CHAT_MESSAGE_JSON_FIELDS = [
  'attachments',
  'email',
  'steps',
  'artifacts',
  'citations',
  'usage',
] as const

// ── Agent → thread update (chat bridge → ChatStore.chatAgentUpdate RPC) ──────

export const chatAgentUpdateSchema = z.object({
  messageId: z.string(),
  threadId: z.string(),
  /** Full accumulated text so far (overwrite semantics). */
  content: z.string().optional(),
  steps: z.array(agentStepSchema).optional(),
  artifacts: z.array(agentArtifactSchema).optional(),
  citations: z.array(messageCitationSchema).optional(),
  usage: messageUsageSchema.optional(),
  error: assistantMessageErrorSchema.optional(),
  completedAt: z.string().optional(),
  status: agentStatusSchema,
})
export type ChatAgentUpdate = z.infer<typeof chatAgentUpdateSchema>

// ── Chat model modes ────────────────────────────────────────────────────────

/** Composer mode → OpenRouter model. Normal is the default (cheap + fast). */
export const chatModeSchema = z.enum(['normal', 'high', 'max'])
export type ChatMode = z.infer<typeof chatModeSchema>

export const CHAT_MODE_MODELS: Record<ChatMode, string> = {
  normal: 'z-ai/glm-5.3-flash',
  high: 'anthropic/claude-sonnet-5.5',
  max: 'anthropic/claude-opus-5.5',
}

export function isChatMode(value: unknown): value is ChatMode {
  return value === 'normal' || value === 'high' || value === 'max'
}

/** Reverse lookup for the per-message usage details (model slug → mode). */
export function modeForModel(model: string | undefined): ChatMode | null {
  if (!model) return null
  for (const [mode, slug] of Object.entries(CHAT_MODE_MODELS) as [ChatMode, string][]) {
    if (model.includes(slug)) return mode
  }
  return null
}

// ── Thread activity (chat room → project presence room) ─────────────────────

/**
 * Which chat threads are busy right now, broadcast to everyone online in the
 * project through the `threadActivity` Y.Map of the `<projectId>:presence`
 * room (key = threadId). The chat room writes it on agent state changes and
 * removes the entry once the turn is over, so the sidebar can mark threads
 * that are currently being worked on.
 */
export const THREAD_ACTIVITY_MAP = 'threadActivity'

/**
 * Project-wide "the chat thread list changed" signal, also on the presence
 * room: `projectMeta.threadsChangedAt` is stamped by the worker whenever a
 * thread is created, renamed, pinned, deleted or receives a message, so
 * every member's sidebar reloads its list without a page refresh.
 */
export const PROJECT_META_MAP = 'projectMeta'
export const THREADS_CHANGED_AT_KEY = 'threadsChangedAt'

export const threadActivitySchema = z.object({
  threadId: z.string(),
  state: agentStateSchema,
  updatedAt: z.string(),
})
export type ThreadActivity = z.infer<typeof threadActivitySchema>

/** States that count as "the thread is active" for the sidebar indicator. */
export const ACTIVE_THREAD_STATES: ReadonlySet<AgentState> = new Set<AgentState>([
  'queued',
  'thinking',
  'reasoning',
  'tool-calling',
  'responding',
])
